import { Localization } from "../Localization.class";
import { labelHelper, trField, urlHelper } from "helpers/themeHelpers";
import { BaseSeoAdapter } from "./BaseSeoAdapter";
import EssentialData from "../../../../private/classes/EssentialData.class";
import { ISeoCache, ISeoFaq, ISeoPage, ITypeBreadcrumb } from "../../types/types";
import { ICountryAssetLite } from "models/Assets/ICountryAsset";
import { EnumScreenNames } from "../../../../enums/EnumScreenNames";
import SpecsSeoModel, {
  EnumSpecsSeoModelPage,
  getByPageName
} from "../../models/SpecsSeoModel";
import { ISelectedAutoServiceCenterObjects } from "dataLayer/filters/autoServicesCenters";
import { ISelectedTabletMobileSpecsObjects } from "dataLayer/filters/tabletMobileSpecs";
import { ISelectedCarPricesSpecsObjects } from "dataLayer/filters/carPricesSpecs";
import { IServiceCenter } from "models/autoServices";
import { breadcrumbGenerate, generateBreadcrumbsSchemaOrg, generateFAQSchemaOrg } from "../../helpers/initSeoData";
import { ICarPricesSpecsCarSpecs } from "models/SpecsModals/carPricesSpecs";
import { IOtherModels } from "models/mobileTabletSpecs";

function cleanFqa(FQA: ISeoFaq[]): ISeoFaq[] {
  FQA = (FQA || []).filter((obj) => {
    return obj.answer != "" && obj.question != "";
  });
  return FQA;
}

function generateISeoPageModel(localization, specsSeoModel: SpecsSeoModel, breadcrumbs, FQA, payload: {}) {

  const link2 = breadcrumbs[breadcrumbs.length - 1].url2;
  const canonical = breadcrumbs[breadcrumbs.length - 1].url;
  let data: ISeoPage = {
    pageTitle: specsSeoModel?.[`title_${localization.lang}`],
    pageH1: specsSeoModel?.[`slogan_1_${localization.lang}`],
    pageH2: specsSeoModel?.[`slogan_2_${localization.lang}`],
    pageArticle: specsSeoModel?.[`description_${localization.lang}`],
    pageMetaDescription: trField(specsSeoModel, "meta_desc", localization.lang),
    canonical,
    link2,
    breadcrumbs,
    FQA: cleanFqa(FQA),
    "schema.org": [
      generateBreadcrumbsSchemaOrg(breadcrumbs),
      generateFAQSchemaOrg(cleanFqa(FQA)),
    ],
  };

  let dataString = JSON.stringify(data);
  Object.keys(payload).forEach(key => {
    let value = payload[key];
    dataString = dataString.replaceAll(`__${key}__`, value);
  });
  return JSON.parse(dataString);
}

export class SpecsSeoAdapter extends BaseSeoAdapter {
    public  localization: Localization
  constructor(
    private readonly seoCache: ISeoCache,
    private readonly country: ICountryAssetLite,
    essentialData: EssentialData,
  ) {
    super(essentialData);
    this.seoCache = seoCache;
    this.country = country;
  }

  setLocalization(localization: Localization): void {
    this.localization =  localization;
  }

   getAutoServiceData(
    screenKey: EnumScreenNames.AUTOS_SERVICE_SCREEN,
    payload: {
      selectedObjects: ISelectedAutoServiceCenterObjects;
      serviceCenterDetails: IServiceCenter;
    }
  ) {
    let { selectedObjects, serviceCenterDetails } = payload;
    selectedObjects.city = this.essentialData.getCityById(
      Number(selectedObjects?.city?.id || serviceCenterDetails.city_id)
    );
    selectedObjects.neighborhood =
      this.essentialData.getNeighborhoodFromCitiesByNeighborhoodId(
        Number(
          selectedObjects?.neighborhood?.id ||
            serviceCenterDetails.neighborhood_id
        )
      );

    let pageName = EnumSpecsSeoModelPage.SC_LANDING;
    if (selectedObjects.city) pageName = EnumSpecsSeoModelPage.SC_CITY;
    if (selectedObjects.neighborhood)
      pageName = EnumSpecsSeoModelPage.SC_NEIGNBORHOOD;
    if (selectedObjects.carMake) pageName = EnumSpecsSeoModelPage.SC_CAR_MAKE;
    if (selectedObjects.service) pageName = EnumSpecsSeoModelPage.SC_SERVICE;
    if (payload.serviceCenterDetails) {
      if (
        !selectedObjects.city &&
        !selectedObjects.neighborhood &&
        !selectedObjects.service &&
        !selectedObjects.carMake
      ) {
        pageName = EnumSpecsSeoModelPage.SC_SERIVCES_LIST;
      } else {
        pageName = EnumSpecsSeoModelPage.SC_CAR_MAKE_LIST;
      }
    }

    let breadcrumbs: ITypeBreadcrumb[];
    let speciality;
    let centerName = "";

    let FAQ = [];
     let brands = '';
    if (selectedObjects.pageType == "details") {
      pageName = EnumSpecsSeoModelPage.SC_CENTER_PAGE;
      FAQ = this.getFQAAutosService(payload.serviceCenterDetails);

      breadcrumbs = this.breadcrumbAutosServices(
        screenKey,
        selectedObjects.city,
        selectedObjects.neighborhood
      );
      const otherLang = this.localization.lang == "ar" ? "en" : "ar";

      const link1 = [
        this.localization.lang,
        this.localization.trPlaceholder(
          screenKey,
          "base_url",
          {},
          this.localization.lang
        ),
        this.localization.trPlaceholder(
          screenKey,
          "url_centers",
          {},
          this.localization.lang
        ),
        payload.serviceCenterDetails?.id +
          "-" +
          payload.serviceCenterDetails?.[`url_${this.localization.lang}`],
      ];
      const link2 = [
        otherLang,
        this.localization.trPlaceholder(screenKey, "base_url", {}, otherLang),
        this.localization.trPlaceholder(
          screenKey,
          "url_centers",
          {},
          otherLang
        ),
        payload.serviceCenterDetails?.id +
          "-" +
          urlHelper(payload.serviceCenterDetails, otherLang),
      ];

      [speciality=null] = payload.serviceCenterDetails?.specialities || [];
      centerName = payload.serviceCenterDetails?.name || "";

      breadcrumbs.push({
        url: "/" + link1.join("/"),
        url2: "/" + link2.join("/"),
        label: labelHelper(
          payload.serviceCenterDetails,
          this.localization.lang
        ),
      });
      brands = serviceCenterDetails.cpOptions.slice(0,4)
          .map((ele) => {
            return labelHelper(ele, this.localization.lang);
          }).toString() + (serviceCenterDetails.cpOptions.length > 5?'...':'');
    } else {
      breadcrumbs = this.breadcrumbAutosServices(
        screenKey,
        selectedObjects.city,
        selectedObjects.neighborhood,
        selectedObjects.service,
        selectedObjects.carMake
      );

      speciality = selectedObjects.service;
    }

    const specsSeoModel: SpecsSeoModel = getByPageName(
      this.seoCache,
      pageName
    );

    return generateISeoPageModel(
      this.localization,
      specsSeoModel,
      breadcrumbs,
      FAQ,
      {
        brands,
        center_name: centerName,
        speciality: labelHelper(speciality, this.localization.lang),
        neighborhood: labelHelper(
          selectedObjects.neighborhood,
          this.localization.lang
        ),
        city: labelHelper(selectedObjects.city, this.localization.lang),
        brand: labelHelper(selectedObjects.carMake, this.localization.lang),
        service: labelHelper(selectedObjects.service, this.localization.lang),
        country: labelHelper(this.country, this.localization.lang),
      }
    );
  }

  async getCarPrices(
    screenKey: EnumScreenNames.CAR_PRICES_SCREEN,
    selectedObjects: ISelectedCarPricesSpecsObjects,
    carSpecs: ICarPricesSpecsCarSpecs
  ) {
    let pageName = EnumSpecsSeoModelPage.CAR_LANDING;
    const crumbs = [];
    crumbs.push({
      url_name_ar: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "ar"
      ),
      url_name_en: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "en"
      ),
      label_ar: this.localization.trPlaceholder(
        screenKey,
        "page_name",
        null,
        "ar"
      ),
      label_en: this.localization.trPlaceholder(
        screenKey,
        "page_name",
        null,
        "en"
      ),
    });
    let FAQ = [];
    if (selectedObjects.make) {
      crumbs.push(selectedObjects.make);
      pageName = EnumSpecsSeoModelPage.CAR_MAKE;
    }

    if (selectedObjects.model) {
      crumbs.push(selectedObjects.model);
      pageName = EnumSpecsSeoModelPage.CAR_MODEL;
    }

    if (selectedObjects.year) {
      crumbs.push(selectedObjects.year);
      pageName = EnumSpecsSeoModelPage.CAR_YEAR;
      FAQ = this.getFQACars(selectedObjects, carSpecs);
    }

    const specsSeoModel: SpecsSeoModel = getByPageName(this.seoCache, pageName);

    const breadcrumbs = breadcrumbGenerate(crumbs, this.localization.lang);

    return generateISeoPageModel(
      this.localization,
      specsSeoModel,
      breadcrumbs,
      FAQ,
      {
        country: labelHelper(this.country, this.localization.lang),
        make: labelHelper(selectedObjects.make, this.localization.lang),
        model: labelHelper(selectedObjects.model, this.localization.lang),
        current_year: new Date().getFullYear(),
        year: labelHelper(selectedObjects.year, this.localization.lang),
        body_type:'',
      }
    );
  }

  async getMobileTabletData(
    screenKey: EnumScreenNames,
    selectedObjects: ISelectedTabletMobileSpecsObjects,
    prices: IOtherModels,
    otherModesl: IOtherModels
  ) {
    const isTablet = selectedObjects.mainPage.id == "tablet";

    let pageName = isTablet
      ? EnumSpecsSeoModelPage.TABLET_LANDING
      : EnumSpecsSeoModelPage.MOBILE_LANDING;
    let crumbs = [];
    let FAQ = [];
    crumbs.push({
      url_name_ar: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "ar"
      ),
      url_name_en: this.localization.trPlaceholder(
        screenKey,
        "base_url",
        null,
        "en"
      ),
      label_ar: this.localization.trPlaceholder(
        screenKey,
        "page_name",
        null,
        "ar"
      ),
      label_en: this.localization.trPlaceholder(
        screenKey,
        "page_name",
        null,
        "en"
      ),
    });

    crumbs.push({
      url_name_ar: selectedObjects.mainPage.url_name_ar,
      url_name_en: selectedObjects.mainPage.url_name_en,
      label_ar: selectedObjects.mainPage.label,
      label_en: selectedObjects.mainPage.label_en,
    });

    if (selectedObjects.make) {
      pageName = isTablet
        ? EnumSpecsSeoModelPage.TABLET_MAKE
        : EnumSpecsSeoModelPage.MOBILE_MAKE;
      crumbs.push(selectedObjects.make);
    }

    if (selectedObjects.model) {
      pageName = isTablet
        ? EnumSpecsSeoModelPage.TABLET_MODEL
        : EnumSpecsSeoModelPage.MOBILE_MODEL;
      crumbs.push(selectedObjects.model);
      FAQ = this.getFQAMobile(selectedObjects, prices, otherModesl);
    }

    const specsSeoModel: SpecsSeoModel = getByPageName(this.seoCache, pageName);

    const breadcrumbs = breadcrumbGenerate(crumbs, this.localization.lang);

    return generateISeoPageModel(
      this.localization,
      specsSeoModel,
      breadcrumbs,
      FAQ,
      {
        country: labelHelper(this.country, this.localization.lang),
        make: labelHelper(selectedObjects.make, this.localization.lang),
        current_year: new Date().getFullYear(),
        model: labelHelper(selectedObjects.model, this.localization.lang),
        condition: labelHelper(
          selectedObjects.condition,
          this.localization.lang
        ),
        storage: labelHelper(
          selectedObjects.storageOption,
          this.localization.lang
        ),
      }
    );
  }

  private breadcrumbAutosServices(
    screenKey: EnumScreenNames,
    city,
    neighborhood,
    service=null,
    carMake = null
  ): ITypeBreadcrumb[] {
    let otherLang: string = this.localization.lang == "ar" ? "en" : "ar";

    const breadcrumbs: ITypeBreadcrumb[] = [];
    let page_name = this.localization.trPlaceholder(screenKey, "page_name");
    let base_url = this.localization.trPlaceholder(screenKey, "base_url");
    let url_service = this.localization.trPlaceholder(screenKey, "url_service");

    let uri = "/" + this.localization.lang + "/" + base_url;
    let uri2 =
      "/" +
      otherLang +
      "/" +
      this.localization.trPlaceholder(screenKey, "base_url", {}, otherLang);
    breadcrumbs.push({
      label: page_name,
      url: uri,
      url2: uri2,
    });
    if (city) {
      uri = uri.concat("/").concat(urlHelper(city, this.localization.lang));
      uri2 = uri2.concat("/").concat(urlHelper(city, otherLang));
      breadcrumbs.push({
        label: labelHelper(city, this.localization.lang),
        url: uri,
        url2: uri2,
      });

      if (neighborhood) {
        const neighborhoodUrl = urlHelper(
          this.essentialData.getNeighborhoodFromCitiesByNeighborhoodId(
            neighborhood.id
          ),
          this.localization.lang
        );
        const neighborhoodUrlOther = urlHelper(
          this.essentialData.getNeighborhoodFromCitiesByNeighborhoodId(
            neighborhood.id
          ),
          otherLang
        );
        uri = uri.concat("/", neighborhoodUrl);
        uri2 = uri2.concat("/", neighborhoodUrlOther);

        breadcrumbs.push({
          label: labelHelper(neighborhood, this.localization.lang),
          url: uri,
          url2: uri2,
        });
      }
    }
    uri = uri.concat("/", url_service);
    uri2 = uri2.concat(
      "/",
      this.localization.trPlaceholder(screenKey, "url_service", {}, otherLang)
    );

    if (service) {
      uri = uri.concat("/", urlHelper(service, this.localization.lang));
      uri2 = uri2.concat("/", urlHelper(service, otherLang));

      breadcrumbs.push({
        label: labelHelper(service, this.localization.lang),
        url: uri,
        url2: uri2,
      });
    }
    if (carMake) {
      uri = uri.concat("/", urlHelper(carMake, this.localization.lang));
      uri2 = uri2.concat("/", urlHelper(carMake, otherLang));

      breadcrumbs.push({
        label: labelHelper(carMake, this.localization.lang),
        url: uri,
        url2: uri2,
      });
    }

    return breadcrumbs.filter(Boolean);
  }

  private generateNotAvailable(): string {
    return this.localization.lang == "en" ? "not available" : "غير متوفر";
  }

  private setFAQ(
    screenKey: EnumScreenNames,
    questionName: string,
    questionNum: number,
    payload: any
  ): string {
    let selectedObject;
    if (screenKey == EnumScreenNames.CAR_PRICES_SCREEN) {
      selectedObject = payload.selectedObject as ISelectedCarPricesSpecsObjects;
      let carSpecs = payload.carSpecs as ICarPricesSpecsCarSpecs;
      return this.localization.trPlaceholder(
        screenKey,
        questionName + "_" + questionNum,
        {
          current: `${labelHelper(
            selectedObject.make,
            this.localization.lang
          )} ${labelHelper(
            selectedObject.model,
            this.localization.lang
          )} ${labelHelper(selectedObject.year, this.localization.lang)}`,
          country: `${labelHelper(
            this.localization.country,
            this.localization.lang
          )}`,
          max_price: carSpecs.highest_price || this.generateNotAvailable(),
          min_price: carSpecs.lowest_price || this.generateNotAvailable(),
          avg_price: carSpecs.average_price || this.generateNotAvailable(),
          body_type: labelHelper(carSpecs?.class_name, this.localization.lang) || null,
          weight: carSpecs.weight  || null,
          origin: labelHelper(carSpecs.origin, this.localization.lang) || null,
          doors: labelHelper(carSpecs.body_style, this.localization.lang) || null,
          engine: carSpecs.engine || null,
          transmission: labelHelper(carSpecs.gearbox, this.localization.lang) || null,
        },
        this.localization.lang
      );
    }
    if (screenKey == EnumScreenNames.MOBILE_TABLET_PRICES_SPECS_SCREEN) {
      let prices = payload.prices;
      let otherModels = payload.otherModels;
      let related = "";
      otherModels.forEach((other, i) => {
        let str = "";
        str = `${   trField(other,'make',this.localization.lang)} ${trField(other,'model',this.localization.lang)}
        ${i == otherModels.length - 1 ? "" : ", "}`;
        related += str;
      });

      return this.localization.trPlaceholder(
        screenKey,
        questionName + "_" + questionNum,
        {
          current: `${ trField(prices,'make',this.localization.lang)} ${
              trField(prices,'model',this.localization.lang)
          }`,
          country: `${labelHelper(
            this.localization.country,
            this.localization.lang
          )}`,
          max_price: prices.price3 || this.generateNotAvailable(),
          min_price: prices.price1 || this.generateNotAvailable(),
          avg_price: prices.price2 || this.generateNotAvailable(),
          currency: this.country?.[`currency_${this.localization.lang}`],
          related,
        },
        this.localization.lang
      );
    }

    if (screenKey == EnumScreenNames.AUTOS_SERVICE_SCREEN) {
      let serviceCenterDetails = payload.serviceCenterDetails as IServiceCenter;
      return this.localization.trPlaceholder(
        screenKey,
        questionName + "_" + questionNum,
        {
          current: `${labelHelper(
            serviceCenterDetails,
            this.localization.lang
          )}`,
          country: `${labelHelper(
            this.localization.country,
            this.localization.lang
          )}`,
          address: `${serviceCenterDetails.address}`,
          neighborhood: `${labelHelper(
            serviceCenterDetails.neighborhood,
            this.localization.lang
          )}`,
          cityName: `${labelHelper(
            serviceCenterDetails.city,
            this.localization.lang
          )}`,
          phone_number: `${serviceCenterDetails.mobile_1},${serviceCenterDetails.mobile_2}`,
          hours: serviceCenterDetails.openingHoursSpecification
            .map((workingDay) => {
              return `${workingDay.dayOfWeek} ${
                this.localization.lang == "ar" ? "من" : "from"
              } ${workingDay.opens} ${
                this.localization.lang == "ar" ? "الى" : "to"
              } ${workingDay.closes}`;
            })
            .toString(),
          // workingDays:
          services: labelHelper(
            serviceCenterDetails.specialities[0],
            this.localization.lang
          ),
          cars: serviceCenterDetails.cpOptions
            .map((ele) => {
              return labelHelper(ele, this.localization.lang);
            })
            .toString(),
          off: serviceCenterDetails.daysDetails.closingTimes,
          fax: serviceCenterDetails.fax_number ,
        },
        this.localization.lang
      );
    }
  }

  private getFQACars(selectedObject, carSpecs:ICarPricesSpecsCarSpecs) {
    const PRICE_QUESTION = "cars_fq_price";
    const PRICE_ANSWER = "cars_fa_price";
    const SPECS_QUESTION = "cars_fq_specs";
    const SPECS_ANSWER = "cars_fa_specs";
    const PRICES_QUESTIONS_NUMBER = 3;
    const SPECS_QUESTIONS_NUMBER = 6;
    let fqaSpecs = [];
    this.range(1, SPECS_QUESTIONS_NUMBER)?.forEach((number) => {
      fqaSpecs.push({
        question: this.setFAQ(
          EnumScreenNames.CAR_PRICES_SCREEN,
          SPECS_QUESTION,
          number,
          {
            selectedObject,
            carSpecs,
          }
        ),
        answer: this.setFAQ(
          EnumScreenNames.CAR_PRICES_SCREEN,
          SPECS_ANSWER,
          number,
          { selectedObject, carSpecs }
        ),
      });
    });

    let fqaPrices = [];
    this.range(1, PRICES_QUESTIONS_NUMBER)?.forEach((number) => {
      fqaPrices.push({
        question: this.setFAQ(
          EnumScreenNames.CAR_PRICES_SCREEN,
          PRICE_QUESTION,
          number,
          {
            selectedObject,
            carSpecs,
          }
        ),
        answer: this.setFAQ(
          EnumScreenNames.CAR_PRICES_SCREEN,
          PRICE_ANSWER,
          number,
          { selectedObject, carSpecs }
        ),
      });
    });
    return fqaPrices.concat(fqaSpecs);
  }

  private getFQAMobile(selectedObjects, prices, otherModels) {
    const PRICE_QUESTION = "mobiles_fq_price";
    const PRICE_ANSWER = "mobiles_fa_price";
    const PRICES_QUESTIONS_NUMBER = 4;
    let fqaPrices = [];
    this.range(1, PRICES_QUESTIONS_NUMBER)?.forEach((number) => {
      fqaPrices.push({
        question: this.setFAQ(
          EnumScreenNames.MOBILE_TABLET_PRICES_SPECS_SCREEN,
          PRICE_QUESTION,
          number,
          {
            selectedObjects,
            prices,
            otherModels,
          }
        ),
        answer: this.setFAQ(
          EnumScreenNames.MOBILE_TABLET_PRICES_SPECS_SCREEN,
          PRICE_ANSWER,
          number,
          {
            selectedObjects,
            prices,
            otherModels,
          }
        ),
      });
    });
    return fqaPrices;
  }

  private getFQAAutosService(serviceCenterDetails: IServiceCenter) {
    const AUTOS_QUESTION = "car_services_fq";
    const AUTOS_ANSWER = "car_services_fa";
    const AUTOS_QUESTIONS_NUMBER = 7;
    let fqaAutos = [];
    this.range(1, AUTOS_QUESTIONS_NUMBER)?.forEach((number) => {
      fqaAutos.push({
        question: this.setFAQ(
          EnumScreenNames.AUTOS_SERVICE_SCREEN,
          AUTOS_QUESTION,
          number,
          { serviceCenterDetails }
        ),
        answer: this.setFAQ(
          EnumScreenNames.AUTOS_SERVICE_SCREEN,
          AUTOS_ANSWER,
          number,
          { serviceCenterDetails }
        ),
      });
    });
    return fqaAutos;
  }

  private range(startAt = 0, size) {
    return [...Array(size).keys()].map((i) => i + startAt);
  }
}
